import { useCallback, useState, type CSSProperties } from 'react';
import { TransportBar } from './TransportBar';
import { RhythmPatternGrid } from './RhythmPatternGrid';
import { useRhythmPlayback } from '../hooks/useRhythmPlayback';
import type { RhythmChallenge } from '../data/rhythmChallenges';
import {
  applyRhythmFigureToBeat,
  createEmptyRhythmPattern,
  resizeRhythmPattern,
} from '../utils/rhythmPattern';
import {
  patternHasHits,
  saveCustomRhythm,
  type CustomRhythmChallenge,
} from '../utils/customRhythms';
import { cloneRhythmPattern } from '../utils/rhythmCompare';
import type { NoteFigure } from '../types';

interface Props {
  onBack: () => void;
  onUseWithClass: (challenge: RhythmChallenge) => void;
  editChallenge?: CustomRhythmChallenge | null;
  accentColor?: string;
}

export function RhythmCreatorView({
  onBack,
  onUseWithClass,
  editChallenge,
  accentColor = '#7b1fa2',
}: Props) {
  const [title, setTitle] = useState(editChallenge?.title ?? '');
  const [description, setDescription] = useState(editChallenge?.description ?? '');
  const [bpm, setBpm] = useState(editChallenge?.bpm ?? 72);
  const [measureCount, setMeasureCount] = useState(
    editChallenge?.pattern.measures.length ?? 1,
  );
  const [beatsPerMeasure, setBeatsPerMeasure] = useState(
    editChallenge?.pattern.beatsPerMeasure ?? 4,
  );
  const [selectedFigure, setSelectedFigure] = useState<NoteFigure>('negra');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [pattern, setPattern] = useState(() =>
    editChallenge
      ? cloneRhythmPattern(editChallenge.pattern)
      : createEmptyRhythmPattern(1, 4),
  );
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  const { playing, activeIndex, play, stop } = useRhythmPlayback(
    pattern,
    bpm,
    soundEnabled,
  );

  const syncLayout = useCallback((beats: number, measures: number) => {
    setPattern((prev) => resizeRhythmPattern(prev, measures, beats));
  }, []);

  const handleApplyFigure = (measureIndex: number, beatIndex: number) => {
    setPattern((prev) => ({
      ...prev,
      measures: prev.measures.map((m, mi) =>
        mi !== measureIndex
          ? m
          : applyRhythmFigureToBeat(m, beatIndex, selectedFigure, prev.beatsPerMeasure),
      ),
    }));
  };

  const handleCellToggle = (
    measureIndex: number,
    beatIndex: number,
    cellIndex: number,
  ) => {
    setPattern((prev) => ({
      ...prev,
      measures: prev.measures.map((m, mi) => {
        if (mi !== measureIndex) return m;
        return {
          slots: m.slots.map((slot, bi) => {
            if (bi !== beatIndex || !slot.active) return slot;
            return {
              ...slot,
              cells: slot.cells.map((c, ci) =>
                ci === cellIndex ? { ...c, hit: !c.hit } : c,
              ),
            };
          }),
        };
      }),
    }));
  };

  const buildChallenge = (): RhythmChallenge | null => {
    if (!title.trim()) {
      setSaveMsg('Escribe un nombre para el ritmo.');
      return null;
    }
    if (!patternHasHits(pattern)) {
      setSaveMsg('Marca al menos un sonido en las casillas.');
      return null;
    }
    return {
      id: editChallenge?.id ?? '',
      title: title.trim(),
      description: description.trim() || 'Ritmo creado para la clase',
      bpm,
      pattern,
    };
  };

  const handleSave = () => {
    const draft = buildChallenge();
    if (!draft) return;
    saveCustomRhythm({ ...draft, id: editChallenge?.id });
    setSaveMsg('¡Ritmo guardado!');
    setTimeout(() => setSaveMsg(null), 2500);
  };

  const handleUseWithClass = () => {
    const draft = buildChallenge();
    if (!draft) return;
    const saved = saveCustomRhythm({ ...draft, id: editChallenge?.id });
    stop();
    onUseWithClass(saved);
  };

  const cssVars = {
    '--accent': accentColor,
    '--rhythm-accent': accentColor,
  } as CSSProperties;

  return (
    <div className="app app--discover app--creator" style={cssVars}>
      <header className="app-header">
        <div>
          <button type="button" className="btn-back" onClick={onBack}>
            ← Retos
          </button>
          <h1 className="app-title">
            {editChallenge ? 'Editar ritmo' : 'Crear ritmo'}
          </h1>
          <p className="app-subtitle">Arma el patrón que los estudiantes descubrirán</p>
        </div>
      </header>

      <section className="creator-meta">
        <label className="creator-field">
          Nombre del reto
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ej. Ritmo de marcha"
            maxLength={60}
          />
        </label>
        <label className="creator-field">
          Descripción (opcional)
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Una pista para los estudiantes"
            maxLength={120}
          />
        </label>
        <div className="creator-layout-row">
          <label className="creator-field creator-field--inline">
            Compás
            <select
              value={beatsPerMeasure}
              onChange={(e) => {
                const n = Number(e.target.value);
                setBeatsPerMeasure(n);
                syncLayout(n, measureCount);
              }}
            >
              <option value={2}>2/4</option>
              <option value={3}>3/4</option>
              <option value={4}>4/4</option>
            </select>
          </label>
          <label className="creator-field creator-field--inline">
            Compases
            <select
              value={measureCount}
              onChange={(e) => {
                const n = Number(e.target.value);
                setMeasureCount(n);
                syncLayout(beatsPerMeasure, n);
              }}
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </label>
        </div>
      </section>

      <p className="hint">
        Toca un <strong>tiempo</strong> para elegir la figura (negra, corchea…). Toca cada
        casilla para marcar <strong>sonido</strong> o silencio. Luego guarda o úsalo con la
        clase.
      </p>

      {saveMsg && (
        <p className="creator-msg" role="status">
          {saveMsg}
        </p>
      )}

      <TransportBar
        bpm={bpm}
        onBpmChange={setBpm}
        figure={selectedFigure}
        onFigureChange={setSelectedFigure}
        playing={playing}
        onPlay={play}
        onStop={stop}
        soundEnabled={soundEnabled}
        onSoundToggle={() => setSoundEnabled((s) => !s)}
        isFullscreen={false}
        onFullscreenToggle={() => {}}
        hideFullscreen
      />

      <RhythmPatternGrid
        pattern={pattern}
        accentColor={accentColor}
        activeIndex={activeIndex}
        selectedFigure={selectedFigure}
        discoverLayout
        onCellToggle={handleCellToggle}
        onApplyFigure={handleApplyFigure}
      />

      <footer className="creator-footer">
        <button type="button" className="btn btn-secondary" onClick={handleSave}>
          💾 Guardar
        </button>
        <button type="button" className="btn btn-primary" onClick={handleUseWithClass}>
          ▶ Usar con la clase
        </button>
      </footer>
    </div>
  );
}
